import Head from 'next/head';
import { useEffect } from 'react';
import type { NextPage } from 'next';
import { env } from '@zopauk/nextjs-env';
import { Modules } from '@/components/Modules';
import { renderMetaTags } from 'react-datocms';
import { logB2BEvent } from '@/utils/analytics';
import { useSession } from '@zopauk/nextjs-auth';
import Events from '@/utils/amplitudeEvents.json';
import { getB2BPageName } from '@/utils/b2bPageName';
import { withSession } from '@/components/SessionPage';
import { usePageData } from '@/utils/hooks/usePageData';
import { isBusinessRoute } from '@/utils/business-route';
import { PreviewToast } from '@/components/preview/Toast';
import { PageBreadcrumbs } from '@/components/Breadcrumbs';
import { StructuredData } from '@/components/StructuredData';
import { LoadingSection } from '@/components/LoadingSection';
import type { Channels } from '@/components/Calculator/types';
import { CalculatorProvider } from '@/components/Calculator/Context';
import { B2BCrossLinks } from '@/components/B2BCrossLinks/B2BCrossLinks';
import { buildBreadcrumbSchema, getPageBreadcrumbs } from '@/utils/breadcrumbs';
import { type PageModules, type PageContext, serverSideProps } from '@/utils/models/page';
import { getMinimum, getMaximum, getChannel, getAmount, getTerms, getTerm } from '@/components/Calculator/defaults';
import {
  extractLandingPageFaqModules,
  generateLandingPageFaqSchema,
  generateOrganizationSchema,
  generateBreadcrumbSchema,
  generateWebSiteSchema,
  generateServiceSchema,
  safeJsonLdStringify,
  B2B_SERVICE_SCHEMAS,
  extractFaqModules,
  generateFaqSchema,
} from '@/utils/schema';

const Page: NextPage<PageContext> = (context) => {
  const { data: session } = useSession();
  const { data } = usePageData(context);
  const isB2B = isBusinessRoute(`/${context.pageSlug}`);

  useEffect(() => {
    if (!isB2B) {
      return;
    }
    logB2BEvent(Events.B2B_PAGE_VIEW, { page_name: getB2BPageName(context.pageSlug) });
  }, [context.pageSlug, isB2B]);

  if (!session && context.authenticate) {
    return <LoadingSection />;
  }

  const faqModules = data?.page?.modular ? extractFaqModules(data.page.modular) : [];
  const faqSchema = isB2B && faqModules.length > 0 ? generateFaqSchema(faqModules.flatMap((m) => m.faqs)) : null;
  const landingPageFaqModules = data?.page?.modular ? extractLandingPageFaqModules(data.page.modular) : [];
  const landingPageFaqSchema =
    landingPageFaqModules.length > 0
      ? generateLandingPageFaqSchema(landingPageFaqModules.flatMap((m) => m.faqs))
      : null;
  const orgSchema = context.pageSlug === 'business' ? generateOrganizationSchema() : null;
  const webSiteSchema = context.pageSlug === 'business' ? generateWebSiteSchema() : null;
  const breadcrumbSchema = isB2B ? generateBreadcrumbSchema(context.pageSlug) : null;
  // The business pages keep their own BreadcrumbList, so that a page has exactly one.
  // TODO: use these breadcrumbs for the business pages too (remove generateBreadcrumbSchema), in a separate task
  const breadcrumbs = data?.page && !isB2B ? getPageBreadcrumbs(data.page) : [];
  const serviceSchema = B2B_SERVICE_SCHEMAS[context.pageSlug]
    ? generateServiceSchema({
        ...B2B_SERVICE_SCHEMAS[context.pageSlug],
        url: data?.page?.canonicalUrl ?? `${env('NEXT_PUBLIC_WEB_GALLERIA_URL')}/${context.pageSlug}`,
      })
    : null;

  return (
    <CalculatorProvider<PageModules>
      data={{
        defaultSelectedTerm: getTerm(data?.page?.terms as string[]),
        channel: (data?.page?.channel as Channels) || getChannel(),
        terms: getTerms(data?.page?.terms as string[]),
        maximum: data?.page?.maximum || getMaximum(),
        minimum: data?.page?.minimum || getMinimum(),
        defaultAmount: getAmount(),
      }}
      modules={data?.page?.modular}
    >
      <Head>
        {data?.page?.seo ? renderMetaTags(data.page.seo) : null}
        {data?.page?.indexable === false ? <meta content="noindex" name="robots" /> : null}
        <link
          href={
            data?.page?.canonicalUrl ||
            `${env('NEXT_PUBLIC_WEB_GALLERIA_URL')}${context.pageSlug === 'home' ? '' : `/${context.pageSlug}`}`
          }
          rel="canonical"
        />
        <StructuredData structuredData={data?.page?.structuredData as undefined | object} />
        {orgSchema ? (
          <script dangerouslySetInnerHTML={{ __html: safeJsonLdStringify(orgSchema) }} type="application/ld+json" />
        ) : null}
        {webSiteSchema ? (
          <script dangerouslySetInnerHTML={{ __html: safeJsonLdStringify(webSiteSchema) }} type="application/ld+json" />
        ) : null}
        {faqSchema ? (
          <script dangerouslySetInnerHTML={{ __html: safeJsonLdStringify(faqSchema) }} type="application/ld+json" />
        ) : null}
        {landingPageFaqSchema ? (
          <script
            dangerouslySetInnerHTML={{ __html: safeJsonLdStringify(landingPageFaqSchema) }}
            type="application/ld+json"
          />
        ) : null}
        <StructuredData structuredData={buildBreadcrumbSchema(breadcrumbs)} />
        {breadcrumbSchema ? (
          <script
            dangerouslySetInnerHTML={{ __html: safeJsonLdStringify(breadcrumbSchema) }}
            type="application/ld+json"
          />
        ) : null}
        {serviceSchema ? (
          <script dangerouslySetInnerHTML={{ __html: safeJsonLdStringify(serviceSchema) }} type="application/ld+json" />
        ) : null}
      </Head>
      {data?.page?.modular ? <Modules<PageModules, PageContext> modules={data.page.modular} context={context} /> : null}
      <PageBreadcrumbs breadcrumbs={breadcrumbs} />
      {isB2B ? <B2BCrossLinks currentPath={`/${context.pageSlug}`} /> : null}
      <PreviewToast />
    </CalculatorProvider>
  );
};

export const getServerSideProps = serverSideProps;

export default withSession<PageContext>(Page);
